import { useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { XStack } from '@/components/ui';

import {
  parseMeetingFilterText,
  uniqueMeetingFilterValues,
} from '../routine-meeting-triggers';

export function RoutineMeetingFilterInput({
  label,
  placeholder,
  values,
  separator,
  onValuesChange,
}: {
  label: string;
  placeholder: string;
  values: string[];
  // Typing it adds a chip, and a pasted list splits on it. Leave it out when a value can
  // contain it
  separator?: string;
  onValuesChange: (values: string[]) => void;
}) {
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const shownValues = uniqueMeetingFilterValues(values);

  const commitDraft = () => {
    const nextValues = uniqueMeetingFilterValues([
      ...shownValues,
      ...parseMeetingFilterText(draft, separator),
    ]);
    setDraft('');
    if (nextValues.length > shownValues.length) onValuesChange(nextValues);
  };

  return (
    <XStack className="flex-wrap items-center gap-2">
      <span className="w-32 shrink-0 text-sm text-foreground-muted">
        {label}
      </span>
      <XStack
        role="group"
        aria-label={label}
        className="min-h-8 min-w-40 flex-1 cursor-text flex-wrap items-center gap-1 rounded-lg border border-border-strong bg-surface px-2 py-1 shadow-sm focus-within:border-accent"
        onClick={() => inputRef.current?.focus()}
      >
        {shownValues.map((value) => (
          <span
            key={value}
            className="inline-flex h-6 items-center gap-1 rounded-full bg-grayA-3 pl-2 pr-1.5 text-sm text-foreground"
          >
            {value}
            <button
              type="button"
              aria-label={`Remove ${value}`}
              onClick={() =>
                onValuesChange(shownValues.filter((item) => item !== value))
              }
              className="inline-flex size-3 shrink-0 items-center justify-center text-foreground-muted hover:text-foreground"
            >
              <Icons.cancel className="size-3" aria-hidden="true" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          aria-label={label}
          autoComplete="off"
          value={draft}
          placeholder={shownValues.length === 0 ? placeholder : undefined}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commitDraft}
          onKeyDown={(event) => {
            // Enter or Backspace while composing (IME) edits the composed text
            if (event.nativeEvent.isComposing) return;
            if (event.key === 'Enter' || event.key === separator) {
              event.preventDefault();
              commitDraft();
            } else if (
              event.key === 'Backspace' &&
              draft === '' &&
              shownValues.length > 0
            ) {
              event.preventDefault();
              onValuesChange(shownValues.slice(0, -1));
            }
          }}
          className="h-6 min-w-24 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-foreground-muted"
        />
      </XStack>
    </XStack>
  );
}
